import type { ConstantsAdminUserStruct } from "@gosaas/types";
import { IconSearch, IconUsers } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useStaffGuard } from "@/components/admin/staff-guard";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useFetchAdminUsers } from "@/components/queries/admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

export function adminListPageCount(total: number, pageSize: number): number {
	if (pageSize <= 0) return 1;
	return Math.max(1, Math.ceil(total / pageSize));
}

function UserRow({ user }: { user: ConstantsAdminUserStruct }) {
	return (
		<TableRow>
			<TableCell>
				<Link
					to="/admin/users/$userId"
					params={{ userId: user.id ?? "" }}
					className="flex items-center gap-2 hover:underline"
				>
					<span className="text-lg">
						{user.emoji ? decodeURIComponent(user.emoji) : "👤"}
					</span>
					<span className="font-medium">{user.email}</span>
				</Link>
			</TableCell>
			<TableCell>
				{[user.first_name, user.last_name].filter(Boolean).join(" ") || "—"}
			</TableCell>
			<TableCell>
				<div className="flex gap-1 flex-wrap">
					{user.is_verified ? (
						<Badge variant="secondary">Verified</Badge>
					) : (
						<Badge variant="outline">Unverified</Badge>
					)}
					{user.mfa_enabled && <Badge variant="secondary">MFA</Badge>}
					{user.staff_role && <Badge>{user.staff_role}</Badge>}
				</div>
			</TableCell>
			<TableCell className="text-muted-foreground">
				{user.created_at
					? new Date(user.created_at).toLocaleDateString("en-US", {
							month: "short",
							day: "numeric",
							year: "numeric",
						})
					: "—"}
			</TableCell>
		</TableRow>
	);
}

export function AdminUsers() {
	const { isStaff, isLoading } = useStaffGuard();
	const [searchInput, setSearchInput] = useState("");
	const [query, setQuery] = useState("");
	const [page, setPage] = useState(1);
	const { userList, isLoadingUsers } = useFetchAdminUsers(query, page);

	if (isLoading || !isStaff) {
		return <Loader />;
	}

	const total = userList?.total ?? 0;
	const pageSize = userList?.page_size ?? 25;
	const pageCount = adminListPageCount(total, pageSize);

	const submitSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setQuery(searchInput.trim());
		setPage(1);
	};

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Admin", href: "/admin" },
					{ title: "Users", href: "/admin/users" },
				]}
				title="Users"
				description="Search and inspect user accounts"
				icon={<IconUsers className="size-6 text-primary" aria-hidden />}
			/>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
						<div>
							<CardTitle>All Users</CardTitle>
							<CardDescription className="mt-1">
								{total} account{total === 1 ? "" : "s"}
							</CardDescription>
						</div>
						<form onSubmit={submitSearch} className="flex items-center gap-2">
							<Input
								placeholder="Search by email or name…"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								className="md:w-72"
							/>
							<Button type="submit" variant="outline" size="icon">
								<IconSearch className="size-4" aria-hidden />
							</Button>
						</form>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					{isLoadingUsers ? (
						<Loader />
					) : (
						<>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Email</TableHead>
										<TableHead>Name</TableHead>
										<TableHead>Status</TableHead>
										<TableHead>Joined</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{(userList?.users ?? []).length === 0 && (
										<TableRow>
											<TableCell
												colSpan={4}
												className="text-center text-muted-foreground py-8"
											>
												No users found.
											</TableCell>
										</TableRow>
									)}
									{(userList?.users ?? []).map((user) => (
										<UserRow key={user.id} user={user} />
									))}
								</TableBody>
							</Table>
							<div className="flex items-center justify-between pt-4">
								<p className="text-sm text-muted-foreground">
									Page {page} of {pageCount}
								</p>
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										disabled={page <= 1}
										onClick={() => setPage((p) => Math.max(1, p - 1))}
									>
										Previous
									</Button>
									<Button
										variant="outline"
										size="sm"
										disabled={page >= pageCount}
										onClick={() => setPage((p) => p + 1)}
									>
										Next
									</Button>
								</div>
							</div>
						</>
					)}
				</CardContent>
			</Card>
		</PageLayout>
	);
}

/* istanbul ignore start -- TanStack Router file route registration */
export const Route = createFileRoute("/_authorized/admin/users/")({
	component: AdminUsers,
});
/* istanbul ignore end */
